<template>
    <div>
        <!-- TODO: Style this like the bordered inputs -->
        <div v-if="!hideTitle">
            {{ field.title }}
        </div>
        <div class="d-flex mb-4 file-gap">
            <div class="thumbnail-wrapper">
                <app-thumbnail 
                    :file="file" 
                    :can-delete="true" 
                    @delete="deleteFile" />
                <div v-if="file?.isImage" class="thumbnail-action-buttons">
                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <v-btn 
                                class="thumbnail-action-button" 
                                v-bind="attrs" 
                                v-on="on" 
                                @click="triggerFileDialog">
                                <v-icon dark color="black">
                                    mdi-pencil
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Upload new image</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                            <v-btn 
                                class="thumbnail-action-button" 
                                v-bind="attrs" 
                                v-on="on" 
                                @click="cropperDialog=true">
                                <v-icon dark color="black">
                                    mdi-crop
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Crop image</span>
                    </v-tooltip>
                </div>

                <app-file-drop 
                    v-show="!file" 
                    ref="fileDrop" 
                    @input="onInput" />
            </div>
        </div>
        
        <v-dialog     
            v-model="cropperDialog" 
            width="auto"
            max-width="600">
            <v-card color="white">
                <v-card-title class="image-card-title">
                    <span>{{ file?.name }}</span>
                </v-card-title>
                <v-card-text class="img-container-wrapper">
                    <div    
                        class="img-container"
                        :style="{ height: containerHeight + 'px'}">
                        <img 
                            v-if="cropperDialog"
                            ref="image"
                            :src="imageUrl"
                            alt=""
                            width="auto"
                            @load="initCropper">                            
                    </div>
                </v-card-text>
                
                <v-card-actions>
                    <app-button
                        color="secondary" 
                        text
                        @click="cropperDialog=false">
                        Close
                    </app-button>
                    <v-spacer />
                    <app-button
                        type="secondary"
                        text
                        @click="resetImage">
                        Reset
                    </app-button>
                    <app-button
                        type="submit"
                        text
                        @click="cropImage">
                        Save
                    </app-button>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import formFieldMixin from "./formFieldMixin";
import { get } from "@/features/schemas/services/schemaApi";
import { imageUrl } from '@/services/imageService';
import Cropper from 'cropperjs/dist/cropper.esm.js';
import 'cropperjs/dist/cropper.css';
import { uploadFile } from "@/services/fileService";
import AppFileDrop from "@/components/AppFileDrop.vue";

export default {
    conponents: { AppFileDrop },
    types: [ "file" ],
    mixins: [formFieldMixin],
    props: {
        value: {
            type: [String, Number],
            default: null
        },
        hideTitle: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            file : null,
            cropperDialog : false,
            showCropper : false,
            cropper : null,
            originalFile: null,
            containerHeight: 300,
        }
    },
    computed: {
        imageUrl() {
            return this.file?.id ? imageUrl(this.file.id) : null;
        }
    },
    watch: {
        cropperDialog: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.$nextTick(() => {
                        const image = this.file;
                        if (!image) return;

                        // Destroy existing cropper if it exists
                        if (this.cropper) {
                            this.cropper.destroy();
                        }
                        
                        // Check if image is already loaded
                        if (image.complete) {
                            this.initCropper();
                        } else {
                            // Wait for image to load
                            image.onload = this.initCropper;
                        }
                    });
                } else if (this.cropper) {
                    this.cropper.destroy();
                    this.cropper = null;
                }
            }
        },
        value: {
            immediate: true,
            async handler() {
                if (!this.value) {
                    return;
                }
                await this.getFile();
            }
        }
    },
    beforeDestroy() {
        if (this.cropper) {
            this.cropper.destroy();
        }
    },
    methods: {
        async deleteFile() {
            this.file = null;
            this.originalFile = null;
            this.$emit("input", null);
        },
        async getFile() {
            this.file = await get("file", this.value);
            if(!this.originalFile) {
                this.originalFile = this.file;
            }
        },
        onInput(file) {
            this.file = file;
            if (this.file.isImage) {
                this.cropperDialog = true;
            }
            this.$emit("input", file.id); // Save initially 
        },
        initCropper() {
            const image = this.$refs.image;
            if (!this.file) return;

            // Destroy existing cropper if it exists
            if (this.cropper) {
                this.cropper.destroy();
            }

            this.cropper = new Cropper(image, {
                aspectRatio          : 1,
                viewMode             : 1,
                responsive           : true,
                restore              : false,
                zoomOnWheel          : false,
                minContainerWidth    : 300,
                minContainerHeight   : 300,
                quality              : 1,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: 'high',
                onLoad: () => {
                    this.containerHeight = Math.min(image.naturalHeight, 300);
                },
                ready: () => {
                    // Funky logic to get crop box to start with full height and centered
                    const width = image.height;
                    const x = (image.width - width) / 2;
                    this.cropper.setData({
                        x: x,
                        y: 0,
                        width: width,
                        height: image.height
                    });
                },
            });
        },
        cropImage() {
            if (!this.cropper) return;

            this.cropper.getCroppedCanvas().toBlob(
                async (blob) => {
                    const croppedFile = new File([blob], this.originalFile.name, {
                        type: this.originalFile.type
                    });
                    const uploadedfile = await uploadFile(croppedFile);
                    this.file = uploadedfile;
                    //await deleteFile(this.originalFile.id);
                    this.$emit("input", uploadedfile.id);
                    this.cropperDialog = false;
                },
            );
        },
        resetImage() {
            if (!this.cropper) return;
            this.file = this.originalFile;            
        },
        triggerFileDialog() {
            this.$refs.fileDrop.openFileExplorer();
        },
    }
}
</script>
<style scoped>
.d-flex {
    display: flex;
}

.file-gap {
    gap          : 10px;
    display      : flex;
    flex-wrap    : wrap;
    margin-bottom: 16px;
}

.align-center {
    align-items: center; 
}

.thumbnail-wrapper {
    display: flex;
    flex-direction: column; 
    align-items: center; 
    margin-right: 16px; 
}

.thumbnail-action-button {
    margin: 8px; 
}

.img-container {
    margin: 0;
    padding: 0;
}

.img-container-wrapper {
  overflow: hidden; /* Hide any content exceeding dialog height */
  height: 400px;    /* Set a desired maximum height, adjust as needed */
}

.img-container {
    margin: 0;
    padding: 0;
}

.img-container-wrapper {
  overflow: hidden; /* Hide any content exceeding dialog height */
  height: 400px;    /* Set a desired maximum height, adjust as needed */
}
</style>
